import { useEffect, FC, CSSProperties } from 'react'
import { CopyOutlined } from '@ant-design/icons'
import { Tag, Row, Col } from 'antd'
import { formatDate } from '@ProdWb/logic'
import { FORBIDDEN_MESSAGE } from '@OpsWb/constants'

import { OPS_PRODUCTION_PATH } from '~/redux-modules/preferences'
import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import Popover from '~/core/Popover'
import FileList from '~/components/FileUploadList'
import {
    ExceptionFailureNote,
    useProjectMemberCheckLazyQuery,
} from '~/gql/graphql'
import colors from '~/styles/_colors.css'

import { getExceptionTag } from './logic'
import { PLACEMENT } from './constants'
import {
    ExceptionAttachmentType,
    JobRequestsExceptionsRecordType,
} from './types'
import style from './styles.css'

type JobRequestsExceptionsProjectType =
    JobRequestsExceptionsRecordType['project']

export const ExceptionStatusTag: FC<{
    reportState: string | undefined
    customeStyle?: CSSProperties | undefined
}> = ({ reportState, customeStyle }) => {
    if (!reportState) {
        return null
    }
    const tag = getExceptionTag(reportState)

    return (
        <Tag style={customeStyle} color={tag?.color}>
            {tag?.title}
        </Tag>
    )
}

export const ExceptionNote: FC<{
    latestNote: ExceptionFailureNote | null
    projectDetails?: JobRequestsExceptionsProjectType
}> = ({ latestNote, projectDetails }) => {
    const [
        checkProjectMember,
        {
            loading: currentProjectMemberLoading,
            data: currentProjectMemberData,
            error,
        },
    ] = useProjectMemberCheckLazyQuery()

    useEffect(() => {
        if (projectDetails && projectDetails.isSecurePlus) {
            checkProjectMember({ variables: { id: projectDetails.id } })
        }
    }, [checkProjectMember, projectDetails])

    if (!latestNote) {
        return <CopyOutlined className={style.notesIconStyleDisabled} />
    }

    const { body, createdBy, createdAt, exceptionFailureNoteAttachments } =
        latestNote

    return error?.graphQLErrors[0].message === FORBIDDEN_MESSAGE ||
        (!currentProjectMemberLoading &&
            !currentProjectMemberData?.project?.currentUserIsMember) ? (
        <Popover
            trigger="click"
            content={
                <Box direction="col">
                    <Box style={{ color: colors.success }}>
                        {createdBy?.displayName}
                    </Box>
                    <Box style={{ marginTop: '8px' }}>{body}</Box>
                    {exceptionFailureNoteAttachments && (
                        <Box className={style.exceptionAttchmentStyle}>
                            <FileList
                                files={
                                    exceptionFailureNoteAttachments as unknown as ExceptionAttachmentType[]
                                }
                                modalControls={{
                                    backFwd: true,
                                    rotate: true,
                                    zoom: true,
                                    canBeDeleted: false,
                                    newWindow: true,
                                }}
                                modalCss={style.imageViewerModalStyle}
                                modalTitle
                                typeOfModule={'woExceptionList'}
                                showIcons={false}
                                hideDateTime
                                small
                            />
                        </Box>
                    )}
                </Box>
            }
            overlayStyle={{
                width: '370px',
                zIndex: 1000,
                overflow: 'auto',
            }}
            title={
                <Row>
                    <Col span={12}>Latest note</Col>
                    <Col span={12}>{formatDate(createdAt)}</Col>
                </Row>
            }
        >
            <CopyOutlined className={style.notesIconStyle} />
        </Popover>
    ) : null
}

export const JobRequestName: FC<{
    jobName: string | null
    jobId: number
    rfqNumber: string | null
}> = ({ jobName, jobId, rfqNumber }) => {
    return (
        <>
            <Link
                name="job-request-name"
                data-test-target="job-request-name-link"
                placement={PLACEMENT}
                target="_blank"
                href={`${OPS_PRODUCTION_PATH}/job-details/${jobId}`}
            >
                {jobName}
            </Link>
            {rfqNumber && (
                <Box direction="row" display="flex" alignItems="center">
                    <Text className={style.rfqTextStyle}>RFQ#{rfqNumber}</Text>
                </Box>
            )}
        </>
    )
}
